import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Stack, Avatar, IconButton, Tooltip, TextField, InputAdornment, CircularProgress,
    Dialog, DialogContent, DialogActions, Snackbar, Alert,
} from '@mui/material';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PRIMARY, PRIMARY_LIGHT } from '../../theme';
import { fmtDayDate, initialsFromName as initials, avatarTone } from '../../utils/format';
import { ghostBtn, field, card, EmptyState, SkeletonRows } from '../uiKit';
import {
    INK, MUTED, LINE, LINE_STRONG, HOVER, RADIUS,
    StatusPill, MissingTag, PunchTimes, Reason, Segmented, Label,
    approveBtn, rejectBtn, statusOf, dayParts, pickList,
} from './punchUi';
import http, { apiErrorMessage } from '../../Api/http';
import { GetPunchRequests, ApprovePunchRequest, RejectPunchRequest } from '../../Api/Api';

const FILTERS = [
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'all', label: 'All' },
];

// Fixed track keeps every column aligned down the page; narrow screens scroll
// the table sideways rather than wrapping cells into a ragged stack.
const COLS = 'minmax(200px, 1.5fr) minmax(140px, 1fr) 190px minmax(180px, 1.6fr) 208px';
const MIN_TABLE_WIDTH = 980;
const PAD_X = 2.5;

// One strip of figures reads lighter than four filled tiles, and keeps the
// summary from outweighing the table it describes.
function SummaryStrip({ items }) {
    return (
        <Box
            sx={{
                ...card, mb: 2, overflow: 'hidden',
                display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            }}
        >
            {items.map((it, i) => (
                <Stack
                    key={it.label}
                    direction="row"
                    spacing={1.4}
                    sx={{
                        alignItems: 'center', px: 2.2, py: 1.8, minWidth: 0,
                        borderLeft: { xs: i % 2 ? `1px solid ${LINE}` : 'none', md: i ? `1px solid ${LINE}` : 'none' },
                        borderTop: { xs: i >= 2 ? `1px solid ${LINE}` : 'none', md: 'none' },
                    }}
                >
                    <Box
                        sx={{
                            width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
                            bgcolor: it.bg, border: `1px solid ${it.color}26`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <it.icon sx={{ fontSize: 19, color: it.color }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, color: INK, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
                            {it.value}
                        </Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#5B6472' }} noWrap>{it.label}</Typography>
                        <Typography sx={{ fontSize: 11, color: MUTED }} noWrap>{it.sub}</Typography>
                    </Box>
                </Stack>
            ))}
        </Box>
    );
}

const normalizeRequest = (it) => ({
    id: it.id ?? it.punchRequestId ?? it.requestId,
    employeeCode: it.employeeCode ?? it.employeeId ?? '',
    employeeName: it.employeeName ?? it.fullName ?? '',
    department: it.department ?? '',
    date: it.date ?? it.punchDate ?? it.attendanceDate ?? '',
    missingType: String(it.missingType ?? it.type ?? '').toLowerCase(),
    loginTime: it.loginTime ?? it.requestedLoginTime ?? it.checkIn ?? '',
    logoutTime: it.logoutTime ?? it.requestedLogoutTime ?? it.checkOut ?? '',
    reason: it.reason ?? '',
    status: String(it.status ?? 'pending').toLowerCase(),
    requestedOn: it.requestedOn ?? it.createdOn ?? '',
    actionedBy: it.actionedByName ?? it.approvedByName ?? it.actionedBy ?? '',
    actionedOn: it.actionedOn ?? it.approvedOn ?? '',
    comment: it.comment ?? it.actionComment ?? it.remarks ?? '',
});

const EMPTY_COUNTS = { pending: 0, approved: 0, rejected: 0, all: 0 };

export default function PunchRequestsTab() {
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState(EMPTY_COUNTS);
    const [status, setStatus] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [acting, setActing] = useState(null);

    const [q, setQ] = useState('');
    const [action, setAction] = useState(null);
    const [comment, setComment] = useState('');
    const [snack, setSnack] = useState(null);

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetPunchRequests, { params: { status } });
            if (body?.error) throw new Error(body.message || 'Could not load punch requests.');
            const list = pickList(body?.data).map(normalizeRequest);
            setRows(list);
            // Without counts from the API the tiles would read 0 next to a visible
            // row, so fall back to counting the slice we actually have.
            const apiCounts = body?.data?.counts;
            setCounts(apiCounts && Object.keys(apiCounts).length
                ? { ...EMPTY_COUNTS, ...apiCounts }
                : list.reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), { ...EMPTY_COUNTS, all: list.length }));
            setLoadError('');
        } catch (err) {
            setRows([]);
            setLoadError(apiErrorMessage(err, 'Could not load punch requests.'));
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => { load(); }, [load]);

    const selectStatus = (key) => {
        if (key === status) return;
        setStatus(key);
    };

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return rows;
        return rows.filter((r) => [r.employeeName, r.employeeCode, r.department, r.reason]
            .some((v) => String(v || '').toLowerCase().includes(s)));
    }, [rows, q]);

    const submitAction = async () => {
        if (!action || acting) return;
        const { row, kind } = action;
        setActing(row.id);
        try {
            const url = kind === 'approve' ? ApprovePunchRequest : RejectPunchRequest;
            const { data: body } = await http.post(url, { id: row.id, comment: comment.trim() });
            if (body?.error) throw new Error(body.message || `Could not ${kind} this request.`);
            notify(body?.message || (kind === 'approve'
                ? `Punch written onto ${fmtDayDate(row.date)} for ${row.employeeName}.`
                : `Request from ${row.employeeName} rejected.`));
            setAction(null);
            setComment('');
            await load();
        } catch (err) {
            const code = err?.response?.status;
            if (code === 409) {
                notify('This request has already been actioned by someone else.', 'warning');
                setAction(null);
                setComment('');
                await load();
            } else {
                notify(apiErrorMessage(err, code === 403
                    ? 'You cannot action your own punch request.'
                    : `Could not ${kind} this request.`), 'error');
            }
        } finally {
            setActing(null);
        }
    };

    const KPIS = [
        { label: 'Pending', value: counts.pending, sub: 'Waiting on your review', icon: PendingActionsRoundedIcon, color: '#B45309', bg: '#FFF7ED' },
        { label: 'Approved', value: counts.approved, sub: 'Punch written to attendance', icon: CheckCircleRoundedIcon, color: '#16A34A', bg: '#DCFCE7' },
        { label: 'Rejected', value: counts.rejected, sub: 'Turned down with a reason', icon: CancelRoundedIcon, color: '#E11D48', bg: '#FEE2E2' },
        { label: 'Total', value: counts.all, sub: 'All corrections raised', icon: EventBusyRoundedIcon, color: PRIMARY, bg: PRIMARY_LIGHT },
    ];

    const approving = action?.kind === 'approve';
    const draftRow = action?.row;

    return (
        <Box sx={{ p: 2 }}>
            <SummaryStrip items={KPIS} />

            <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                {/* Filters and search belong to the table, so they live on its
                    toolbar rather than stranded in the page corner. */}
                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center', gap: 1.2, px: PAD_X, py: 1.6,
                        borderBottom: `1px solid ${LINE_STRONG}`, flexWrap: 'wrap',
                    }}
                >
                    {/* The active segment already carries its count — a second
                        "N requests" line here only opened a hole in the middle. */}
                    <Segmented items={FILTERS.map((f) => ({ ...f, count: counts[f.key] }))} value={status} onChange={selectStatus} />

                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
                        <TextField
                            size="small" placeholder="Search name, code or reason"
                            value={q} onChange={(e) => setQ(e.target.value)}
                            sx={{ ...field, width: { xs: '100%', sm: 250 }, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, fontSize: 13 } }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: MUTED }} /></InputAdornment> }}
                        />
                        <Tooltip arrow title="Reload"><span>
                            <IconButton onClick={() => load()} disabled={loading} sx={{ ...ghostBtn, width: 38, height: 38 }}>
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span></Tooltip>
                    </Stack>
                </Stack>

                {loading ? (
                    <SkeletonRows count={5} />
                ) : loadError ? (
                    <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
                        <EventBusyRoundedIcon sx={{ fontSize: 36, color: '#CBD2DD' }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#64748B', mt: 1 }}>Couldn’t load requests</Typography>
                        <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.4 }}>{loadError}</Typography>
                        <Button onClick={() => load()} sx={{ ...ghostBtn, height: 34, px: 2, mt: 1.8 }}>Try again</Button>
                    </Box>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        icon={q ? InboxRoundedIcon : CheckRoundedIcon}
                        title={q ? 'Nothing matches that search' : `No ${status === 'all' ? '' : status} requests`}
                        hint={q
                            ? 'Try another employee name or code.'
                            : 'Employees raise corrections for days where a punch went missing. They land here for review.'}
                    />
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: MIN_TABLE_WIDTH }}>
                            <Box
                                sx={{
                                    display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', gap: 2.5,
                                    px: PAD_X, py: 1.3, bgcolor: '#FAFAFD', borderBottom: `1px solid ${LINE_STRONG}`,
                                }}
                            >
                                <Label>Employee</Label>
                                <Label>Missed day</Label>
                                <Label>Claimed punch</Label>
                                <Label>Reason</Label>
                                <Label sx={{ textAlign: 'right' }}>{status === 'pending' ? 'Decision' : 'Status'}</Label>
                            </Box>

                            {filtered.map((r) => {
                                const busy = acting === r.id;
                                const day = dayParts(r.date);
                                const st = statusOf(r.status);
                                const pending = r.status === 'pending';
                                return (
                                    <Box
                                        key={r.id}
                                        sx={{
                                            display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', gap: 2.5,
                                            px: PAD_X, py: 1.9, borderBottom: `1px solid ${LINE}`,
                                            opacity: busy ? 0.55 : 1, transition: 'background-color .15s, opacity .15s',
                                            '&:hover': { bgcolor: HOVER },
                                            '&:last-of-type': { borderBottom: 'none' },
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', minWidth: 0 }}>
                                            <Avatar sx={{ width: 38, height: 38, ...avatarTone(r.employeeName), fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                                                {initials(r.employeeName)}
                                            </Avatar>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }} noWrap>{r.employeeName || '—'}</Typography>
                                                <Typography sx={{ fontSize: 11.5, color: MUTED }} noWrap>
                                                    {[r.employeeCode, r.department].filter(Boolean).join(' · ') || '—'}
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK, fontVariantNumeric: 'tabular-nums' }} noWrap>
                                                {day.date}
                                            </Typography>
                                            {day.weekday && <Typography sx={{ fontSize: 11.5, color: MUTED }} noWrap>{day.weekday}</Typography>}
                                            <MissingTag type={r.missingType} />
                                        </Box>

                                        {/* Boxed so the pair reads as one claim rather than
                                            two times adrift in a wide column. */}
                                        <Box
                                            sx={{
                                                display: 'inline-flex', px: 1.3, py: 0.9, borderRadius: RADIUS,
                                                bgcolor: '#FAFAFC', border: `1px solid ${LINE}`, width: 'fit-content',
                                            }}
                                        >
                                            <PunchTimes inTime={r.loginTime} outTime={r.logoutTime} />
                                        </Box>

                                        <Box sx={{ minWidth: 0, borderLeft: `2px solid ${LINE_STRONG}`, pl: 1.4 }}>
                                            <Reason text={r.reason} />
                                            {!pending && (r.actionedBy || r.comment) && (
                                                <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.4 }} noWrap>
                                                    {r.actionedBy ? `${st.label} by ${r.actionedBy}` : st.label}
                                                    {r.comment ? ` — ${r.comment}` : ''}
                                                </Typography>
                                            )}
                                        </Box>

                                        <Stack direction="row" spacing={0.8} sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                                            {pending ? (
                                                <>
                                                    <Button
                                                        onClick={() => { setAction({ row: r, kind: 'approve' }); setComment(''); }}
                                                        disabled={Boolean(acting)}
                                                        startIcon={busy
                                                            ? <CircularProgress size={14} sx={{ color: '#15803D' }} />
                                                            : <CheckRoundedIcon sx={{ fontSize: 16 }} />}
                                                        sx={{ ...approveBtn, height: 32, px: 1.5, fontSize: 12.5 }}
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        onClick={() => { setAction({ row: r, kind: 'reject' }); setComment(''); }}
                                                        disabled={Boolean(acting)}
                                                        startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
                                                        sx={{ ...rejectBtn, height: 32, px: 1.4, fontSize: 12.5 }}
                                                    >
                                                        Reject
                                                    </Button>
                                                </>
                                            ) : (
                                                <StatusPill status={r.status} />
                                            )}
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>

            <Dialog
                open={Boolean(action)}
                onClose={() => { if (!acting) { setAction(null); setComment(''); } }}
                maxWidth="xs" fullWidth
                slotProps={{ paper: { sx: { borderRadius: '12px' } } }}
            >
                <Box sx={{ px: 3, pt: 2.8, pb: 0.5 }}>
                    <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center' }}>
                        <Box
                            sx={{
                                width: 38, height: 38, borderRadius: '10px', flexShrink: 0,
                                bgcolor: approving ? '#F0FDF4' : '#FEF3F5',
                                border: `1px solid ${approving ? '#C4EBD1' : '#F3D3DA'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            {approving
                                ? <CheckRoundedIcon sx={{ fontSize: 19, color: '#15803D' }} />
                                : <CloseRoundedIcon sx={{ fontSize: 19, color: '#BE123C' }} />}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 16.5, fontWeight: 800, color: INK }}>
                                {approving ? 'Approve punch request' : 'Reject punch request'}
                            </Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#6B7280' }} noWrap>
                                {draftRow?.employeeName} · {fmtDayDate(draftRow?.date)}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                <DialogContent sx={{ pt: '14px !important' }}>
                    <Box sx={{ border: `1px solid ${LINE}`, borderRadius: RADIUS, px: 1.6, py: 1.3, mb: 1.8 }}>
                        <Label>Claimed punch</Label>
                        <Box sx={{ mt: 0.6 }}>
                            <PunchTimes inTime={draftRow?.loginTime} outTime={draftRow?.logoutTime} />
                        </Box>
                        <Box sx={{ mt: 1.2, pt: 1.2, borderTop: `1px dashed ${LINE}` }}>
                            <Reason text={draftRow?.reason} lines={4} sx={{ fontSize: 12.5 }} />
                        </Box>
                    </Box>

                    {approving && (
                        <Stack direction="row" spacing={1} sx={{ mb: 1.8, alignItems: 'flex-start' }}>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: MUTED, mt: '2px', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>
                                Payroll lock, shift checks and the edit window still apply — if the write is blocked, the request stays pending.
                            </Typography>
                        </Stack>
                    )}

                    <TextField
                        label="Comment (optional)"
                        size="small" fullWidth multiline minRows={3} autoFocus
                        placeholder={approving
                            ? 'e.g. Verified with the site manager'
                            : 'e.g. No supporting evidence for the times claimed'}
                        value={comment} onChange={(e) => setComment(e.target.value)} sx={field}
                    />
                    <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.8 }}>
                        {approving
                            ? 'Kept on the request for the audit trail.'
                            : 'Shown to the employee alongside the rejection.'}
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => { setAction(null); setComment(''); }} disabled={Boolean(acting)} sx={{ ...ghostBtn, height: 40, px: 2 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={submitAction} disabled={Boolean(acting)}
                        startIcon={acting ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null}
                        sx={{
                            height: 40, px: 2.4, fontWeight: 700, textTransform: 'none', borderRadius: RADIUS,
                            boxShadow: 'none', color: '#fff',
                            bgcolor: approving ? '#16A34A' : '#DC2626',
                            '&:hover': { bgcolor: approving ? '#15803D' : '#B91C1C' },
                            '&.Mui-disabled': { bgcolor: approving ? '#A7DDBB' : '#F0AFAF', color: '#fff' },
                        }}
                    >
                        {acting
                            ? (approving ? 'Approving…' : 'Rejecting…')
                            : (approving ? 'Approve' : 'Reject')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={Boolean(snack)} autoHideDuration={3600} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: RADIUS }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
